import { Folder, Plus } from "lucide-react";
import { PaginationFooter } from "./PaginationFooter.tsx";
import { relativeTime } from "../lib/chatGroups.ts";
import type { DashboardProject } from "../lib/homeData.ts";
import { paginate } from "../lib/homeData.ts";
import { navigate, projectPath } from "../lib/router.ts";

interface Props {
  allProjects: DashboardProject[];
  filteredProjects: DashboardProject[];
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onNewProject: () => void;
}

export function RecentProjectsSection({
  allProjects,
  filteredProjects,
  page,
  pageSize,
  onPageChange,
  onNewProject,
}: Props) {
  const paged = paginate(filteredProjects, page, pageSize);

  return (
    <section className="home-section" aria-label="Recent Projects">
      <h2 className="home-section-title">Recent Projects</h2>
      <div className="home-card" data-testid="recent-projects-card">
        {allProjects.length === 0 ? (
          <div className="home-empty-state" data-testid="empty-projects">
            <p className="home-empty-title">No projects yet</p>
            <p className="home-empty-desc muted small">
              Create a new Project to start working.
            </p>
            <button
              className="primary"
              onClick={onNewProject}
              data-testid="empty-new-project"
            >
              <Plus size={14} aria-hidden /> New Project
            </button>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="home-empty-state" data-testid="empty-search-projects">
            <p className="home-empty-title">No matching projects</p>
            <p className="home-empty-desc muted small">
              Try searching with different terms.
            </p>
          </div>
        ) : (
          <>
            <ul className="home-card-list">
              {paged.pageItems.map((p) => {
                const contextParts = [
                  p.workspaceName,
                  p.machineName,
                  !p.workspaceName ? p.path : undefined,
                ].filter(Boolean);

                return (
                  <li key={`${p.machineId}:${p.projectId}`} className="home-row-wrap">
                    <button
                      className="home-list-row"
                      onClick={() => navigate(projectPath(p.machineId, p.projectId))}
                      data-testid={`recent-${p.name}`}
                      title={`Open ${p.name}`}
                    >
                      <span className="home-row-icon">
                        <Folder size={15} aria-hidden />
                      </span>
                      <span className="home-row-main">
                        <span className="home-row-title-line">
                          <span className="home-row-title">{p.name}</span>
                          {!p.online && (
                            <span
                              className="home-offline-badge"
                              title="Machine is currently offline"
                            >
                              offline
                            </span>
                          )}
                        </span>
                        <span className="home-row-subline">
                          {contextParts.map((part, idx) => (
                            <span key={idx} className="home-row-subline-item">
                              {idx > 0 && <span className="home-sep">·</span>}
                              {part}
                            </span>
                          ))}
                        </span>
                      </span>
                      <span className="home-row-meta">
                        <span className="home-row-time">
                          {p.lastActivity
                            ? relativeTime(new Date(p.lastActivity).toISOString())
                            : ""}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <PaginationFooter
              page={page}
              total={filteredProjects.length}
              pageSize={pageSize}
              onPageChange={onPageChange}
              entityName="Projects"
            />
          </>
        )}
      </div>
    </section>
  );
}
